import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "./fetcher.js";
import type { OperatorDecisionRequest, OperatorDecisionResponse } from "./types.js";

/** Phase 15 D-DEC-FLOW-01 -- wait-for-server-then-refetch. NO optimistic update.
 *
 *  On 2xx -> invalidate ['job', jobId] + ['job-events', jobId] keys, which
 *  triggers immediate refetch; the new approval_given / approval_rejected
 *  event lands in the timeline within one polling round-trip.
 *
 *  D-DEC-FLOW-02 -- idempotency-key handled engine-side (Phase 13 D-DEC-01);
 *  SPA emits no X-Idempotency-Key header.
 *
 *  Phase 532 wave 10 task 10.2 -- the request body is serialised whole, so the
 *  optional `gate` on OperatorDecisionRequest reaches the engine with no change
 *  here. On refusal `fetchWithAuth` throws `ApiError`, whose `bodyText` carries
 *  the engine's `{error_code, message}`; the dialog renders that through
 *  `readServerRefusal` (`./serverRefusal.ts` — deliberately DOM-free, so the
 *  node-only test tree can reach it without importing this fetcher) instead of a
 *  generic sentence of its own. No new route:
 *  this is the same POST /internal/v0/jobs/{id}/decisions it has always been.
 */
export function usePostDecision(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: OperatorDecisionRequest) =>
      fetchWithAuth<OperatorDecisionResponse>(`/internal/v0/jobs/${jobId}/decisions`, {
        method: "POST",
        body: JSON.stringify(req),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", jobId] });
      qc.invalidateQueries({ queryKey: ["job-events", jobId] });
    },
  });
}
